import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { Flex, Spin } from 'antd';
import { SelectComponent } from 'baseComponents/SelectComponent';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { TextInputComponent } from 'baseComponents/TextInputComponent';
import { useGetStoresList } from 'hooks/useGetStoresList';
import { RoleType, useGetRoles } from 'hooks/useGetRoles';
import { useGetFranchiseOwners } from 'hooks/useGetFranchiseOwners';
import { SearchableDropdownSelectComponent } from 'baseComponents/SearchableDropdownSelectComponent';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

export type UserFormValues = {
  name: string;
  email: string;
  phone: string;
  role?: number;
  stores?: (number | undefined)[];
  franchiseOwner?: number;
};

type AdminUserFormProps = {
  onSubmit: (data: UserFormValues) => void;
  onCancel?: () => void;
  defaultValues?: Partial<UserFormValues>;
  submitButtonLabel?: string;
  mode?: 'create' | 'edit';
};

export const phoneRegex = /^\+44\d{10}$/;

export const userFormSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please provide correct email.')
    .required('Email is required.'),
  name: Yup.string().required('Name is required.'),
  phone: Yup.string()
    .required('Phone is required.')
    .matches(
      phoneRegex,
      'Please enter a valid UK phone number (e.g. +447123456789)'
    ),
  role: Yup.number().test(
    'required when create mode',
    'Role is required.',
    (value, context) =>
      context.options.context?.mode === 'create' ? !!value : true
  ),
  stores: Yup.array(Yup.number()).test(
    'required when role is agent or FO',
    'Field is required',
    (value, context) => {
      const selectedRoleCode: RoleType['code'] =
        context.options.context?.rolesList.find(
          ({ id }: RoleType) => id === context.parent.role
        )?.code;
      return (selectedRoleCode === 'agent' ||
        selectedRoleCode === 'franchise_owner') &&
        value
        ? value.length > 0
        : true;
    }
  ),
  franchiseOwner: Yup.number().test(
    'required when role is agent',
    'Field is required',
    (value, context) => {
      const selectedRoleCode: RoleType['code'] =
        context.options.context?.rolesList.find(
          ({ id }: RoleType) => id === context.parent.role
        )?.code;
      return selectedRoleCode === 'agent' ? !!value : true;
    }
  ),
});

const initialState = {
  name: '',
  email: '',
  phone: '',
  role: undefined,
  stores: [],
  franchiseOwner: undefined,
};

export const AdminUserForm = ({
  onCancel,
  onSubmit,
  defaultValues,
  submitButtonLabel = 'Submit',
  mode = 'create',
}: AdminUserFormProps) => {
  const [foStores, setFoStores] = useState([]);

  const { data: stores = [], isLoading: areStoresLoading } = useGetStoresList();

  const roles = useGetRoles();
  const rolesList = roles.data ?? [];

  const franchiseOwners = useGetFranchiseOwners();

  const { handleSubmit, control, watch, getValues } = useForm<UserFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      ...initialState,
      ...defaultValues,
    },
    context: { mode, rolesList },
    resolver: yupResolver(userFormSchema),
  });

  const role = watch('role');
  const isAgentRoleSelected =
    rolesList.find(({ id }) => id === role)?.code === 'agent';

  const handleSubmitCallback = (data: any) => {
    onSubmit(data);
  };

  const filteredStores = () => {
    if (stores) {
      return stores.filter((store) => store.is_active);
    }
    return [];
  };

  const getFoStores = (id: number) => {
    const franchiseOwner = franchiseOwners.data?.find(
      (owner) => owner.id === id
    );
    if (franchiseOwner) {
      return franchiseOwner.stores;
    }
    return [];
  };

  const handleChangeFranchiseOwner = (value: number) => {
    const foStores = getFoStores(value);
    setFoStores(foStores);
  };

  return (
    <form
      style={{ flex: 1, flexBasis: '100%' }}
      onSubmit={handleSubmit(handleSubmitCallback, (errors) =>
        console.log(errors)
      )}
    >
      <Flex vertical gap={16} style={{ padding: 2 }}>
        <Controller
          name='name'
          control={control}
          render={({ field, fieldState }) => (
            <TextInputComponent
              label='Name'
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name='email'
          control={control}
          render={({ field, fieldState }) => (
            <TextInputComponent
              label='Email'
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name='phone'
          control={control}
          render={({ field, fieldState }) => (
            <TextInputComponent
              label='Phone'
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />

        {mode === 'create' && (
          <>
            <Controller
              name='role'
              control={control}
              render={({ field, fieldState }) => (
                <Spin spinning={roles.isLoading}>
                  <SelectComponent
                    placeholder='Role select'
                    options={rolesList.map(({ name, id }) => ({
                      label: name,
                      value: id,
                    }))}
                    error={fieldState.error?.message}
                    {...field}
                  />
                </Spin>
              )}
            />
            {isAgentRoleSelected && (
              <Controller
                name='franchiseOwner'
                control={control}
                render={({ field, fieldState }) => (
                  <Spin spinning={franchiseOwners.isLoading}>
                    <SelectComponent
                      placeholder='Franchise owner'
                      onSelect={(value) => handleChangeFranchiseOwner(value)}
                      options={
                        franchiseOwners.data?.map(({ email, id }) => ({
                          label: email,
                          value: id,
                        })) ?? []
                      }
                      error={
                        fieldState.error?.message ||
                        franchiseOwners.error?.response?.data?.message
                      }
                      {...field}
                    />
                  </Spin>
                )}
              />
            )}
          </>
        )}
        {(getValues().role === 4 || getValues().role === 5) && (
          <Controller
            name='stores'
            control={control}
            render={({ field, fieldState }) => (
              <SearchableDropdownSelectComponent
                placeholder='Select stores'
                loading={areStoresLoading}
                options={
                  getValues().role === 5
                    ? foStores.map((store: any) => ({
                        key: store.id,
                        label: store.name,
                        value: store.id,
                      }))
                    : filteredStores().map((store: any) => ({
                        key: store.id,
                        label: store.name,
                        value: store.id,
                      }))
                }
                multiselect
                {...field}
              />
            )}
          />
        )}
      </Flex>

      <Flex gap={8} style={{ maxWidth: 648, marginTop: 64 }}>
        <ButtonComponent
          label={submitButtonLabel}
          appereance='primary'
          size='middle'
          fullWidth
          htmlType='submit'
        />
        <ButtonComponent
          label='Cancel'
          onClick={onCancel}
          appereance='secondary'
          size='middle'
          fullWidth
        />
      </Flex>
    </form>
  );
};
